/**
 * Market decimal precision and orderbook grouping configuration
 *
 * Decimal precision rules:
 * - 0 decimals: For high-value assets (BTC: $100,000+ → $100,000)
 * - 1 decimal: For mid-high value assets ($500-$5000 → $4,572.4)
 * - 2 decimals: For mid-value assets ($100-$500 → $206.93)
 * - 3 decimals: For low-mid value assets ($10-$100 → $24.724)
 * - 4 decimals: For low-value assets ($1-$10 → $3.4690)
 * - 5 decimals: For very low-value assets ($0.1-$1 → $0.82100)
 * - 6 decimals: For micro-value assets (<$0.1 → $0.030000)
 *
 * These precision levels ensure consistent UI display regardless of price fluctuations.
 * We should revisit and adjust default groupings if assets experience major price movements.
 */
interface MarketDecimalConfig {
	symbol: string;
	priceDecimals: number;
	defaultGrouping: number;
	minTickSize?: number;
	isSpecialCase?: boolean;
}

export const MARKET_DECIMAL_CONFIG: Record<string, MarketDecimalConfig> = {
	BTC: {
		symbol: 'BTC',
		priceDecimals: 0,
		defaultGrouping: 1,
		isSpecialCase: true,
	},
	ETH: {
		symbol: 'ETH',
		priceDecimals: 1,
		defaultGrouping: 0.1,
	},
	SOL: {
		symbol: 'SOL',
		priceDecimals: 2,
		defaultGrouping: 0.01,
	},
	BNB: {
		symbol: 'BNB',
		priceDecimals: 1,
		defaultGrouping: 0.1,
	},
	ADA: {
		symbol: 'ADA',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	AVAX: {
		symbol: 'AVAX',
		priceDecimals: 3,
		defaultGrouping: 0.001,
	},
	LINK: {
		symbol: 'LINK',
		priceDecimals: 3,
		defaultGrouping: 0.001,
	},
	LTC: {
		symbol: 'LTC',
		priceDecimals: 2,
		defaultGrouping: 0.01,
	},
	RAY: {
		symbol: 'RAY',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	SUI: {
		symbol: 'SUI',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	TON: {
		symbol: 'TON',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	XRP: {
		symbol: 'XRP',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	TIA: {
		symbol: 'TIA',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	JTO: {
		symbol: 'JTO',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	WIF: {
		symbol: 'WIF',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	DRIFT: {
		symbol: 'DRIFT',
		priceDecimals: 4, // lower due to tick size limitations
		defaultGrouping: 0.0001,
	},
	JUP: {
		symbol: 'JUP',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	OP: {
		symbol: 'OP',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	ARB: {
		symbol: 'ARB',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	SEI: {
		symbol: 'SEI',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	POL: {
		symbol: 'POL',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	DOGE: {
		symbol: 'DOGE',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	PNUT: {
		symbol: 'PNUT',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	MELANIA: {
		symbol: 'MELANIA',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	DYM: {
		symbol: 'DYM',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	TNSR: {
		symbol: 'TNSR',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	PYTH: {
		symbol: 'PYTH',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	AI16Z: {
		symbol: 'AI16Z',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	GOAT: {
		symbol: 'GOAT',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	W: {
		symbol: 'W',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	RLB: {
		symbol: 'RLB',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	KMNO: {
		symbol: 'KMNO',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	LAUNCHCOIN: {
		symbol: 'LAUNCHCOIN',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	FWOG: {
		symbol: 'FWOG',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	'1KWEN': {
		symbol: '1KWEN',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	PENGU: {
		symbol: 'PENGU',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	MICHI: {
		symbol: 'MICHI',
		priceDecimals: 6,
		defaultGrouping: 0.000001,
	},
	MOTHER: {
		symbol: 'MOTHER',
		priceDecimals: 6,
		defaultGrouping: 0.000001,
	},
	PUMP: {
		symbol: 'PUMP',
		priceDecimals: 6,
		defaultGrouping: 0.000001,
	},
	TAO: {
		symbol: 'TAO',
		priceDecimals: 2,
		defaultGrouping: 0.01,
	},
	HYPE: {
		symbol: 'HYPE',
		priceDecimals: 3,
		defaultGrouping: 0.001,
	},
	INJ: {
		symbol: 'INJ',
		priceDecimals: 3,
		defaultGrouping: 0.001,
	},
	'1MPEPE': {
		symbol: '1MPEPE',
		priceDecimals: 3,
		defaultGrouping: 0.001,
	},
	TRUMP: {
		symbol: 'TRUMP',
		priceDecimals: 3,
		defaultGrouping: 0.001,
	},
	IP: {
		symbol: 'IP',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	APT: {
		symbol: 'APT',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	RENDER: {
		symbol: 'RENDER',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	'1KMEW': {
		symbol: '1KMEW',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	HNT: {
		symbol: 'HNT',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	BERA: {
		symbol: 'BERA',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	'1MBONK': {
		symbol: '1MBONK',
		priceDecimals: 3,
		defaultGrouping: 0.001,
	},
	POPCAT: {
		symbol: 'POPCAT',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	FARTCOIN: {
		symbol: 'FARTCOIN',
		priceDecimals: 4, // lower due to tick size limitations
		defaultGrouping: 0.0001,
	},
	ME: {
		symbol: 'ME',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	IO: {
		symbol: 'IO',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	MOODENG: {
		symbol: 'MOODENG',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	CLOUD: {
		symbol: 'CLOUD',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	ZEX: {
		symbol: 'ZEX',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	KAITO: {
		symbol: 'KAITO',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	PAXG: {
		symbol: 'PAXG',
		priceDecimals: 1,
		defaultGrouping: 0.1,
	},
	ASTER: {
		symbol: 'ASTER',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	XPL: {
		symbol: 'XPL',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	'2Z': {
		symbol: '2Z',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	'1KPUMP': {
		symbol: '1KPUMP',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	ZEC: {
		symbol: 'ZEC',
		priceDecimals: 2,
		defaultGrouping: 0.01,
	},
	MNT: {
		symbol: 'MNT',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	MET: {
		symbol: 'MET',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
	'1KMON': {
		symbol: '1KMON',
		priceDecimals: 3,
		defaultGrouping: 0.001,
	},
	LIT: {
		symbol: 'LIT',
		priceDecimals: 4,
		defaultGrouping: 0.0001,
	},
	BP: {
		symbol: 'BP',
		priceDecimals: 5,
		defaultGrouping: 0.00001,
	},
};
/**
 * Get decimal configuration for a market
 */
function getMarketDecimalConfig(symbol: string): MarketDecimalConfig | null {
	// Handle perp markets by stripping any suffixes
	const baseSymbol = symbol.replace('-PERP', '').replace('/USDC', '');
	return MARKET_DECIMAL_CONFIG[baseSymbol] || null;
}
/**
 * Get price decimal places for a market
 */
export function getMarketPriceDecimals(symbol: string): number {
	const config = getMarketDecimalConfig(symbol);
	return config?.priceDecimals ?? 2; // Default to 2 decimal places
}
/**
 * Get default grouping for a market
 */
function getMarketDefaultGrouping(symbol: string): number {
	const config = getMarketDecimalConfig(symbol);
	return config?.defaultGrouping ?? 0.01; // Default to 0.01
}

/**
 * Format price according to market decimal rules
 */
export function formatMarketPrice(
	price: number,
	symbol: string,
	useCommaFormatting?: boolean
): string {
	const decimals = getMarketPriceDecimals(symbol);
	const formattedPrice = price.toFixed(decimals);
	if (useCommaFormatting) {
		return Number(formattedPrice).toLocaleString('en-US', {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		});
	}
	return formattedPrice;
}
/**
 * Get the appropriate orderbook grouping based on market's default grouping and tick size
 * Calculates the correct number of ticks needed to achieve the desired dollar grouping
 */
export function getOrderbookGroupingForMarket(
	symbol: string,
	tickSizeInDollars?: number
): 1 | 10 | 100 | 500 | 1000 {
	const config = getMarketDecimalConfig(symbol);

	if (!config) {
		return 100; // Default grouping
	}

	// If no tick size provided, fall back to reasonable defaults based on grouping
	if (tickSizeInDollars === undefined) {
		const desiredGrouping = config.defaultGrouping;

		// Use reasonable defaults based on the desired grouping level
		if (config.isSpecialCase && desiredGrouping >= 1) {
			return 10; // BTC special case
		} else if (desiredGrouping >= 0.1) {
			return 1; // ETH-level assets
		} else if (desiredGrouping >= 0.01) {
			return 10; // SOL-level assets
		} else if (desiredGrouping >= 0.001) {
			return 10; // HYPE-level assets
		} else {
			return 1; // Very small groupings, use 1 tick
		}
	}

	const desiredGrouping = config.defaultGrouping;

	// If desired grouping is smaller than tick size, use 1 tick (can't go smaller)
	if (desiredGrouping <= tickSizeInDollars) {
		return 1;
	}

	// Calculate how many ticks we need to achieve the desired grouping
	const ticksNeeded = Math.round(desiredGrouping / tickSizeInDollars);

	// The ticksNeeded should naturally be one of the valid options
	// If it's not in the valid options, clamp to reasonable bounds
	if (ticksNeeded <= 1) return 1;
	if (ticksNeeded <= 10) return 10;
	if (ticksNeeded <= 100) return 100;
	if (ticksNeeded <= 500) return 500;
	return 1000;
}

/**
 * Get the effective decimal places for price inputs, considering both market decimal config and tick size
 * Uses the larger of the two (fewer decimal places) to ensure price inputs respect both constraints
 */
export function getEffectivePriceDecimals(
	symbol: string,
	tickSizeDecimalPlaces?: number
): number {
	const marketDecimals = getMarketPriceDecimals(symbol);

	// If no tick size provided, use market decimals
	if (tickSizeDecimalPlaces === undefined) {
		return marketDecimals;
	}

	// Use the minimum of market decimals and tick size decimals (fewer decimal places = larger constraint)
	return Math.min(marketDecimals, tickSizeDecimalPlaces);
}

/**
 * Clean ticker symbol for browser title
 */
export function cleanTickerSymbol(marketName: string): string {
	if (marketName.endsWith('-PERP')) {
		return marketName.replace('-PERP', '');
	} else {
		return marketName.replace('/USDC', '') + '-SPOT';
	}
}

/**
 * Get the effective tick size in dollars, considering both the actual tick size and market configuration
 * Uses the larger of the two to ensure proper price increments
 */
export function getEffectiveTickSizeInDollars(
	marketSymbol: string,
	tickSizeInDollars: number
): number {
	const marketDefaultGrouping = getMarketDefaultGrouping(marketSymbol);

	// Use the larger of tick size or market default grouping as the effective tick size
	return Math.max(tickSizeInDollars, marketDefaultGrouping);
}
